import axios from "axios";
import type { Instance } from "../components/table/types";
import {
  BASE_URLS,
  compareOperatingSystems,
  comparePaymentTypes,
  compareRegions,
  DIRECT_BASE_URLS,
  SUBSCRIPTION_KEY,
} from "../utils/constants";
import { useInstanceStore } from "../store/useInstanceStore";
import { getProviderMapping } from "../utils/utils";
interface SimilarInstanceResult {
  provider: string;
  data: any | null;
  error?: string;
}
const USE_DIRECT_API = true;
const BASE_DOMAIN = USE_DIRECT_API ? "https://data.cloudprice.net" : "";

const api = axios.create({
  baseURL: USE_DIRECT_API ? `${BASE_DOMAIN}/api/v2` : "/",
  headers: {
    Accept: "application/json",
    "subscription-key": SUBSCRIPTION_KEY,
  },
});
const apiAzure = axios.create({
  baseURL: USE_DIRECT_API ? `${BASE_DOMAIN}/api/v1` : "/",
  headers: {
    Accept: "application/json",
    "subscription-key": SUBSCRIPTION_KEY,
  },
});

const similarInstanceApi = axios.create({
  baseURL: USE_DIRECT_API ? `${BASE_DOMAIN}/api/v2` : "/similar-api",
  headers: {
    Accept: "application/json",
    "subscription-key": SUBSCRIPTION_KEY,
  },
});

function getBestProvider(aws?: number, gcp?: number, azure?: number) {
  const prices = [
    { name: "AWS", value: aws },
    { name: "GCP", value: gcp },
    { name: "Azure", value: azure },
  ].filter((p) => typeof p.value === "number");

  if (prices.length === 0) return "-";
  return prices.reduce((prev, curr) =>
    curr.value! < prev.value! ? curr : prev
  ).name;
}

export const fetchInstances = async (
  provider: "aws" | "gcp" | "azure",
  region?: string,
  paymentType?: string,
  operatingSystem?: string
): Promise<Instance[]> => {
  const URL = USE_DIRECT_API ? DIRECT_BASE_URLS : BASE_URLS;
  try {
    const baseUrl =
      provider === "aws" ? URL.aws : provider === "gcp" ? URL.gcp : URL.azure;

    // 🔹 Build query params dynamically
    const params: Record<string, string | number> = {};
    if (region) params.region = region;
    if (paymentType) params.paymentType = paymentType;
    if (operatingSystem) params.operatingSystem = operatingSystem;
    const url = params.toString() ? `${baseUrl}` : baseUrl;
    const callingApi = provider === "azure" ? apiAzure : api;
    const response = await callingApi.get(url, { params });

    let items: any[] = [];

    if (provider === "azure") {
      // Azure structure: response.data.listVmInformations
      items = response.data?.listVmInformations || [];
    } else {
      // AWS & GCP structure: response.data.Data.Items
      items = response.data?.Data?.Items || [];
    }

    const formatted: Instance[] = items.map((item: any, i: number) => {
      // 🔹 Azure fields
      const azureName = item.name;
      const azurePrice = item.linuxPrice ?? item.windowsPrice ?? null;
      const azureMemory = item.memoryInMB;
      const azureVCpu = item.numberOfCores;
      const azureArch = item.cpuArchitecture ?? "x64";

      // 🔹 AWS/GCP fields
      const awsOrGcpType = item.InstanceType;
      const awsOrGcpVcpu = item.ProcessorVCPUCount;
      const awsOrGcpMem = item.MemorySizeInMB;
      const awsOrGcpArch = Array.isArray(item.ProcessorArchitecture)
        ? item.ProcessorArchitecture[0]
        : item.ProcessorArchitecture;
      const awsOrGcpPrice = item.PricePerHour;

      // 🔹 GPU fields
      const gpuMemoryInMB = item.GPUMemoryInMB ?? null;
      const gpuName = item.GPUName ?? "";
      const hasGpu = item.HasGPU ?? false;
      const gpuMemoryGB = gpuMemoryInMB ? gpuMemoryInMB / 1024 : "-";

      // 🔹 Final field resolution
      const instanceType = awsOrGcpType || azureName || "-";
      const rawMemory = awsOrGcpMem ?? azureMemory;
      const memory = rawMemory ? (rawMemory / 1024).toFixed(2) : "-";

      const vcpus = awsOrGcpVcpu ?? azureVCpu ?? "-";
      const cpuArchitecture = awsOrGcpArch ?? azureArch ?? "x86_64";

      const awsPrice = provider === "aws" ? awsOrGcpPrice ?? "-" : "-";
      const gcpPrice = provider === "gcp" ? awsOrGcpPrice ?? "-" : "-";
      const azureFinalPrice = provider === "azure" ? azurePrice ?? "-" : "-";

      return {
        id: `instance-${i}`,
        instanceType,
        instanceFamily: instanceType.split(".")[0]?.toUpperCase() || "-",
        vcpus,
        memory,
        cpuArchitecture,
        gpu: gpuMemoryGB,
        gpuName,
        hasGpu,
        aws: awsPrice,
        gcp: gcpPrice,
        azure: azureFinalPrice,
        best: getBestProvider(
          provider === "aws" ? awsOrGcpPrice : undefined,
          provider === "gcp" ? awsOrGcpPrice : undefined,
          provider === "azure" ? azurePrice : undefined
        ),
      };
    });

    // Pagination slice
    return formatted;
  } catch (error) {
    console.error(`Error fetching ${provider} instances:`, error);
    return [];
  }
};
export async function fetchSimilarInstances(
  provider: "aws" | "gcp" | "azure",
  instanceType: string
): Promise<SimilarInstanceResult[]> {
  try {
    let basePath = "";

    if (provider === "aws") {
      basePath = `/aws/ec2/instances/${instanceType}/similar`;
    } else if (provider === "gcp") {
      basePath = `/gcp/compute/instances/${instanceType}/similar`;
    } else {
      throw new Error(
        "Unsupported provider type. Only 'aws' or 'gcp' allowed."
      );
    }

    // We will compare across these 3 cloud providers
    const compareProviders = ["AWS", "GCP", "Azure"];

    const requests = compareProviders.map((cloudProvider) =>
      similarInstanceApi
        .get(`${basePath}?cloudProvider=${cloudProvider}`)
        .then((res) => ({
          provider: cloudProvider,
          data: res.data,
        }))
        .catch((err) => ({
          provider: cloudProvider,
          data: null,
          error: err.message,
        }))
    );

    const results = await Promise.all(requests);

    return results;
  } catch (error) {
    console.error("Error fetching similar instances:", error);
    return [];
  }
}

export const fetchAllInstances = async (
  providerType: "aws" | "gcp" | "azure",
  page = 1,
  limit = 20,
  region?: string,
  paymentType?: string,
  operatingSystem?: string
): Promise<{
  awsInstances: Instance[];
  gcpInstances: Instance[];
  azureInstances: Instance[];
}> => {
  const {
    setAwsInstances,
    setGcpInstances,
    setAzureInstances,
    setLoading,
    setError,
  } = useInstanceStore.getState();

  setLoading(true);
  setError(null);
  try {
    const regionMapping = getProviderMapping(
      providerType,
      region || "",
      compareRegions
    );

    const paymentMapping = getProviderMapping(
      providerType,
      paymentType || "",
      comparePaymentTypes
    );
    const operatingSystemMapping = getProviderMapping(
      providerType,
      operatingSystem || "",
      compareOperatingSystems
    );

    // 🧠 Fetch all providers in parallel with graceful fallback handling
    const [awsData, gcpData, azureData] = await Promise.allSettled([
      fetchInstances(
        "aws",
        regionMapping.aws,
        paymentMapping?.aws,
        operatingSystemMapping?.aws
      ),
      fetchInstances(
        "gcp",
        regionMapping.gcp,
        paymentMapping?.gcp,
        operatingSystemMapping?.gcp
      ),
      fetchInstances(
        "azure",
        regionMapping.azure,
        paymentMapping?.azure,
        operatingSystemMapping?.azure
      ),
    ]);

    // 🧩 Normalize results safely
    const awsInstances = awsData.status === "fulfilled" ? awsData.value : [];
    const gcpInstances = gcpData.status === "fulfilled" ? gcpData.value : [];
    const azureInstances =
      azureData.status === "fulfilled" ? azureData.value : [];

    // ✅ Update store
    setAwsInstances(awsInstances);
    setGcpInstances(gcpInstances);
    setAzureInstances(azureInstances);

    // ✅ Return so caller can use directly
    return { awsInstances, gcpInstances, azureInstances };
  } catch (err: any) {
    console.error("❌ Error fetching all provider instances:", err);
    setError(err.message || "Unknown error occurred");

    return { awsInstances: [], gcpInstances: [], azureInstances: [] };
  } finally {
    setLoading(false);
  }
};
